import React, { useState, useRef, useEffect } from "react";
import { Loader2 } from "lucide-react";

interface TwoFactorFormProps {
  onVerify: (pin: string) => Promise<boolean>;
  primaryColor?: string;
  theme?: "light" | "dark";
  isSending?: boolean;
  /** Which channel the code actually went out on, as reported by the API. */
  channel?: "whatsapp" | "email";
  /** Masked destination, e.g. "••• ••• 464" or "m•••@flowapt.com". */
  sentTo?: string;
  /** Re-send on a specific channel. Omit to hide the switch-channel link. */
  onResend?: (channel: "whatsapp" | "email") => Promise<void>;
}

const TwoFactorForm: React.FC<TwoFactorFormProps> = ({
  onVerify,
  primaryColor = "#2563eb",
  theme = "light",
  isSending = false,
  channel,
  sentTo,
  onResend,
}) => {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resentTo, setResentTo] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!isSending) {
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    }
  }, [isSending]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const char = value.slice(-1);
    const newDigits = [...digits];
    newDigits[index] = char;
    setDigits(newDigits);
    setError("");

    if (char && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    if (char && index === 3) {
      const pin = [...newDigits.slice(0, 3), char].join("");
      if (pin.length === 4) handleSubmit(pin);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pasted.length === 4) {
      setDigits(pasted.split(""));
      handleSubmit(pasted);
    }
  };

  // A re-send mints a NEW code server-side, so anything already typed is dead.
  const handleResend = async (next: "whatsapp" | "email") => {
    if (!onResend || isResending) return;
    setIsResending(true);
    setError("");
    setDigits(["", "", "", ""]);
    try {
      await onResend(next);
      setResentTo(next === "email" ? "your email" : "WhatsApp");
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } catch {
      setError("Couldn't send a new code. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (pin: string) => {
    setIsVerifying(true);
    setError("");
    try {
      const ok = await onVerify(pin);
      if (!ok) {
        setError("Incorrect code. Please try again.");
        setDigits(["", "", "", ""]);
        setTimeout(() => inputRefs.current[0]?.focus(), 50);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const isDark = theme === "dark";
  const bg = isDark ? "bg-gray-900 text-white" : "bg-white text-gray-900";
  const inputBg = isDark ? "bg-gray-800 border-gray-600 text-white" : "bg-gray-50 border-gray-300 text-gray-900";
  const isEmail = channel === "email";
  const destinationNoun = isEmail ? "email" : "phone";
  const altChannel: "whatsapp" | "email" = isEmail ? "whatsapp" : "email";

  return (
    <div className={`flex flex-col items-center justify-center h-full px-6 gap-6 ${bg}`}>
      <div className="text-center">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
          style={{ backgroundColor: `${primaryColor}20` }}
        >
          {isSending ? (
            <Loader2 size={24} className="animate-spin" style={{ color: primaryColor }} />
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          )}
        </div>

        <h2 className="text-lg font-semibold">
          {isSending ? "Sending your code…" : isEmail ? "Verify your email" : "Verify your number"}
        </h2>
        <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          {isSending
            ? `We're sending a 4-digit code to your ${destinationNoun}`
            : sentTo
              ? `Enter the 4-digit code we sent to ${sentTo}`
              : `Enter the 4-digit code sent to your ${destinationNoun}`}
        </p>
      </div>

      {!isSending && (
        <div className="flex gap-3" onPaste={handlePaste}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              disabled={isVerifying}
              className={`w-12 h-14 text-center text-xl font-bold rounded-lg border-2 outline-none transition-all focus:border-current ${inputBg} ${
                error ? "border-red-400" : ""
              }`}
              style={{
                borderColor: digit && !error ? primaryColor : undefined,
              }}
            />
          ))}
        </div>
      )}

      {error && (
        <p className="text-red-500 text-sm text-center">{error}</p>
      )}

      {isVerifying && (
        <Loader2 className="animate-spin text-gray-400" size={20} />
      )}

      {!isSending && onResend && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => handleResend(altChannel)}
            disabled={isResending || isVerifying}
            className="text-sm underline underline-offset-2 disabled:opacity-50"
            style={{ color: primaryColor }}
          >
            {isResending
              ? "Sending…"
              : altChannel === "email"
                ? "Email me the code instead"
                : "Send the code to WhatsApp instead"}
          </button>
          {resentTo && !isResending && (
            <p className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              New code sent to {resentTo}. Use the newest one.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoFactorForm;
